import React, { useEffect, useState } from "react";
import {
  Button,
  Box,
  IconButton,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import MUIDataTable from "mui-datatables";
import axios from "axios";
import toast from "react-hot-toast";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import Loader from "../../components/loader/Loader";
import { useTableHeight } from "../../hooks/useTablePagination";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";

const Role = () => {
  const [userList, setUserList] = useState([]);
  const [roleMenuRights, setRoleMenuRights] = useState([]);
  const [menuList, setMenuList] = useState([]);
  const [permissionsList, setPermissionsList] = useState([]);
  const [loader, setLoader] = useState(false);
  const [updatedPermissions, setUpdatedPermissions] = useState({});
  const [selectedMenusForDeletion, setSelectedMenusForDeletion] = useState({});
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openEditPermissionDialog, setOpenEditPermissionDialog] =
    useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openSaveDialog, setOpenSaveDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedMenuRight, setSelectedMenuRight] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [newMenuAccess, setNewMenuAccess] = useState({
    menu_ids: [],
    permission_id: "",
    view: 0,
    add: 0,
    edit: 0,
    delete: 0,
  });
  const [editMenuPermission, setEditMenuPermission] = useState({
    permission_id: "",
    view: 0,
    add: 0,
    edit: 0,
    delete: 0,
  });
  const tableHeight = useTableHeight();
  const [open, setOpen] = useState(false);
  const [openRoleDeleteDialog, setOpenRoleDeleteDialog] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [newRoleFormData, setNewRoleFormData] = useState({
    role_name: "",
    description: "",
    active_status: 1,
  });

  // 🔹 Load Admin Users
  const loadAdminUsers = async () => {
    try {
      setLoader(true);
      const res = await axios.get("/roles");
      setUserList(res?.data?.data || []);
    } catch (error) {
      toast.error("Failed to fetch admin users");
    } finally {
      setLoader(false);
    }
  };

  // 🔹 Load Role Menu Rights
  const loadRoleMenuRights = async () => {
    try {
      const res = await axios.get("/roleMenuRights");
      const data = res?.data?.data || [];
      console.log("Role Menu Rights Data:", data);
      setRoleMenuRights(data);
    } catch (error) {
      console.error("Failed to fetch role menu rights:", error);
    }
  };

  // 🔹 Load Available Menus
  const loadMenus = async () => {
    try {
      const res = await axios.get("/menuItems");
      setMenuList(res.data.data || []);
    } catch (error) {
      console.error("Failed to fetch menus:", error);
      setMenuList([]);
    }
  };

  // 🔹 Load Permissions
  const loadPermissions = async () => {
    try {
      const res = await axios.get("/menu_permissions");
      setPermissionsList(res?.data?.data || []);
      console.log("Permissions loaded:", res?.data?.data);
    } catch (error) {
      console.error("Failed to fetch permissions:", error);
      setPermissionsList([]);
    }
  };

  useEffect(() => {
    loadAdminUsers();
    loadRoleMenuRights();
    loadMenus();
    loadPermissions();
  }, []);

  // 🔹 Get permission details by ID
  const getPermissionById = (permissionId) => {
    return permissionsList.find((p) => p.permission_id === permissionId);
  };

  // 🔹 Get menus for a specific role_id
  const getMenusForRole = (roleId) => {
    const menus = roleMenuRights.filter(
      (right) => Number(right.role_id) === Number(roleId),
    );
    console.log(
      `Filtering for role_id: ${roleId}, found ${menus.length} menus`,
    );
    return menus;
  };

  // 🔹 Get unique identifier for menu using role_id, menu_id AND user_id
  const getMenuKey = (menuRight, userId) => {
    return `${userId}_${menuRight.role_id}_${menuRight.menu_id}`;
  };

  // 🔹 Get Current Permission Value
  const getPermissionValue = (menuRight, permissionType, userId) => {
    const menuKey = getMenuKey(menuRight, userId);

    if (
      updatedPermissions[menuKey] !== undefined &&
      updatedPermissions[menuKey][permissionType] !== undefined
    ) {
      return updatedPermissions[menuKey][permissionType];
    }

    return menuRight[permissionType];
  };

  // 🔹 Handle Permission Change (for individual checkboxes in Edit Dialog)
  const handlePermissionChange = (menuRight, permissionType, userId) => {
    const menuKey = getMenuKey(menuRight, userId);
    const currentValue = getPermissionValue(menuRight, permissionType, userId);
    const newValue = currentValue === 1 ? 0 : 1;

    setUpdatedPermissions((prev) => ({
      ...prev,
      [menuKey]: {
        ...prev[menuKey],
        [permissionType]: newValue,
        role_id: menuRight.role_id,
        menu_id: menuRight.menu_id,
      },
    }));
  };

  // 🔹 Check if menu has changes
  const hasChanges = (menuRight, userId) => {
    const menuKey = getMenuKey(menuRight, userId);
    return (
      updatedPermissions[menuKey] &&
      Object.keys(updatedPermissions[menuKey]).length > 0
    );
  };

  // 🔹 Get changes for a specific user
  const getUserChanges = (userId) => {
    return Object.keys(updatedPermissions).filter((key) =>
      key.startsWith(`${userId}_`),
    );
  };

  // 🔹 Toggle menu selection for deletion
  const toggleMenuSelection = (userId, menuRight) => {
    const menuKey = getMenuKey(menuRight, userId);
    setSelectedMenusForDeletion((prev) => ({
      ...prev,
      [menuKey]: !prev[menuKey],
    }));
  };

  // 🔹 Check if menu is selected for deletion
  const isMenuSelected = (userId, menuRight) => {
    const menuKey = getMenuKey(menuRight, userId);
    return !!selectedMenusForDeletion[menuKey];
  };

  // 🔹 Get selected menus for a user
  const getSelectedMenusForUser = (userId) => {
    return Object.keys(selectedMenusForDeletion).filter(
      (key) => key.startsWith(`${userId}_`) && selectedMenusForDeletion[key],
    );
  };

  // 🔹 Clear selections for a user
  const clearUserSelections = (userId) => {
    setSelectedMenusForDeletion((prev) => {
      const copy = { ...prev };
      Object.keys(copy).forEach((key) => {
        if (key.startsWith(`${userId}_`)) {
          delete copy[key];
        }
      });
      return copy;
    });
  };

  // 🔹 Save All Changes for User
  const saveAllPermissions = async (userId) => {
    try {
      const userChanges = getUserChanges(userId);

      if (userChanges.length === 0) {
        toast.error("No changes to save");
        return;
      }

      setLoader(true);

      const updatePromises = userChanges.map(async (menuKey) => {
        const updates = updatedPermissions[menuKey];
        const roleId = updates.role_id;
        const menuId = updates.menu_id;

        const originalMenuRight = roleMenuRights.find(
          (r) => r.role_id === roleId && r.menu_id === menuId,
        );

        const payload = {
          view:
            updates.view !== undefined ? updates.view : originalMenuRight.view,
          add: updates.add !== undefined ? updates.add : originalMenuRight.add,
          edit:
            updates.edit !== undefined ? updates.edit : originalMenuRight.edit,
          delete:
            updates.delete !== undefined
              ? updates.delete
              : originalMenuRight.delete,
        };

        return axios.put(`/roleMenuRights/${roleId}/${menuId}`, payload);
      });

      await Promise.all(updatePromises);

      toast.success(
        `Successfully updated ${userChanges.length} menu permission(s)`,
      );

      setUpdatedPermissions((prev) => {
        const copy = { ...prev };
        userChanges.forEach((key) => delete copy[key]);
        return copy;
      });

      await loadRoleMenuRights();
      setOpenSaveDialog(false);
      setOpenEditDialog(false);
      setPendingAction(null);
    } catch (error) {
      console.error(error);
      toast.error("Failed to update permissions");
    } finally {
      setLoader(false);
    }
  };

  // 🔹 Handle Save Confirmation
  const handleSaveClick = (userId) => {
    setPendingAction({ type: "save", userId });
    setOpenSaveDialog(true);
  };

  const handleConfirmSave = () => {
    if (pendingAction && pendingAction.type === "save") {
      saveAllPermissions(pendingAction.userId);
    }
  };

  const handleCancelSave = () => {
    setOpenSaveDialog(false);
    setPendingAction(null);
  };

  // 🔹 Delete Selected Menus
  const deleteSelectedMenus = async (userId) => {
    try {
      const selectedKeys = getSelectedMenusForUser(userId);

      if (selectedKeys.length === 0) {
        toast.error("No menus selected for deletion");
        return;
      }

      setLoader(true);

      const deletePromises = selectedKeys.map(async (menuKey) => {
        const parts = menuKey.split("_");
        const roleId = parts[1];
        const menuId = parts[2];

        return axios.delete(`/roleMenuRights/${roleId}/${menuId}`);
      });

      await Promise.all(deletePromises);

      toast.success(
        `Successfully deleted ${selectedKeys.length} menu permission(s)`,
      );

      clearUserSelections(userId);

      setUpdatedPermissions((prev) => {
        const copy = { ...prev };
        selectedKeys.forEach((key) => delete copy[key]);
        return copy;
      });

      await loadRoleMenuRights();
      setOpenDeleteDialog(false);
      setOpenEditDialog(false);
      setPendingAction(null);
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete menu permissions");
    } finally {
      setLoader(false);
    }
  };

  // 🔹 Handle Delete Confirmation
  const handleDeleteClick = (userId) => {
    setPendingAction({ type: "delete", userId });
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (pendingAction && pendingAction.type === "delete") {
      deleteSelectedMenus(pendingAction.userId);
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setPendingAction(null);
  };

  // 🔹 Handle Open Edit Dialog
  const handleOpenEditDialog = (user) => {
    console.log("Selected User:", user);
    console.log("User ID:", user.id);
    console.log("All Role Menu Rights:", roleMenuRights);
    console.log("Filtered Menus:", getMenusForRole(user.id));

    setSelectedUser(user);
    setOpenEditDialog(true);
    clearUserSelections(user.id);
  };

  // 🔹 Handle Close Edit Dialog
  const handleCloseEditDialog = () => {
    if (selectedUser) {
      const userChanges = getUserChanges(selectedUser.id);
      setUpdatedPermissions((prev) => {
        const copy = { ...prev };
        userChanges.forEach((key) => delete copy[key]);
        return copy;
      });
      clearUserSelections(selectedUser.id);
    }
    setOpenEditDialog(false);
    setSelectedUser(null);
  };

  // 🔹 Handle Open Add Dialog
  const handleOpenAddDialog = (user) => {
    setOpenEditDialog(false);
    setOpenAddDialog(true);
  };

  // 🔹 Handle Close Add Dialog
  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setNewMenuAccess({
      menu_ids: [],
      permission_id: "",
      view: 0,
      add: 0,
      edit: 0,
      delete: 0,
    });
    setOpenEditDialog(true);
  };

  // 🔹 Handle Permission Selection Change (for permission dropdown in Add Dialog)
  const handlePermissionSelectionChange = (permissionId) => {
    const permission = getPermissionById(permissionId);

    if (permission) {
      setNewMenuAccess((prev) => ({
        ...prev,
        permission_id: permissionId,
        view: permission.view || 0,
        add: permission.add || 0,
        edit: permission.edit || 0,
        delete: permission.delete || 0,
      }));
    }
  };

  // 🔹 Handle Edit Permission
  const handleEditPermission = (menuRight) => {
    setSelectedMenuRight(menuRight);
    setEditMenuPermission({
      permission_id: menuRight.permission_id || "",
      view: menuRight.view,
      add: menuRight.add,
      edit: menuRight.edit,
      delete: menuRight.delete,
    });
    setOpenEditPermissionDialog(true);
  };

  // 🔹 Handle Edit Permission Selection Change
  const handleEditPermissionSelectionChange = (permissionId) => {
    const permission = getPermissionById(permissionId);

    if (permission) {
      setEditMenuPermission((prev) => ({
        ...prev,
        permission_id: permissionId,
        view: permission.view || 0,
        add: permission.add || 0,
        edit: permission.edit || 0,
        delete: permission.delete || 0,
      }));
    }
  };

  // 🔹 Save Edit Permission
  const saveEditPermission = async () => {
    try {
      if (!editMenuPermission.permission_id) {
        toast.error("Please select a permission type");
        return;
      }

      setLoader(true);

      const payload = {
        permission_id: Number(editMenuPermission.permission_id),
      };

      console.log("Updating permission:", payload);

      await axios.put(
        `/roleMenuRights/${selectedMenuRight.role_id}/${selectedMenuRight.menu_id}`,
        payload,
      );

      toast.success("Permission updated successfully");

      await loadRoleMenuRights();
      setOpenEditPermissionDialog(false);
      setSelectedMenuRight(null);
    } catch (error) {
      toast.error("Failed to update permission");
      console.error("Error updating permission:", error);
    } finally {
      setLoader(false);
    }
  };

  // 🔹 Handle Close Edit Permission Dialog
  const handleCloseEditPermissionDialog = () => {
    setOpenEditPermissionDialog(false);
    setSelectedMenuRight(null);
    setEditMenuPermission({
      permission_id: "",
      view: 0,
      add: 0,
      edit: 0,
      delete: 0,
    });
  };

  // 🔹 Get Available Menus for User (excluding already assigned)
  const getAvailableMenusForUser = (user) => {
    const assignedMenuIds = getMenusForRole(user.id).map((m) => m.menu_id);
    return menuList.filter((menu) => !assignedMenuIds.includes(menu.menu_id));
  };

  // 🔹 Handle Change in New Role Form
  const handleNewRoleChange = (e) => {
    const { name, value } = e.target;
    setNewRoleFormData((prev) => ({
      ...prev,
      [name]: name === "active_status" ? Number(value) : value,
    }));
  };

  // 🔹 Open New Role Dialog
  const handleOpenNewRoleDialog = () => {
    setNewRoleFormData({
      role_name: "",
      description: "",
      active_status: 1,
    });
    setOpen(true);
  };

  // 🔹 Close New Role Dialog
  const handleCloseNewRoleDialog = () => {
    setOpen(false);
    setNewRoleFormData({
      role_name: "",
      description: "",
      active_status: 1,
    });
  };

  // 🔹 Create New Role
  const handleCreateRole = async () => {
    try {
      if (!newRoleFormData.role_name.trim()) {
        toast.error("Please enter a role name");
        return;
      }

      if (!newRoleFormData.description.trim()) {
        toast.error("Please enter a description");
        return;
      }

      setLoader(true);

      const payload = {
        role_name: newRoleFormData.role_name,
        description: newRoleFormData.description,
        active_status: newRoleFormData.active_status,
      };

      const response = await axios.post("/roles", payload);

      toast.success("Role created successfully");
      await loadAdminUsers();
      handleCloseNewRoleDialog();
    } catch (error) {
      console.error("Error creating role:", error);
      toast.error(
        error.response?.data?.message || "Failed to create role"
      );
    } finally {
      setLoader(false);
    }
  };

  // 🔹 Delete Role
  const handleDeleteRole = (roleId, roleName) => {
    setRoleToDelete({ id: roleId, name: roleName });
    setOpenRoleDeleteDialog(true);
  };

  // 🔹 Confirm Delete Role
  const handleConfirmDeleteRole = async () => {
    if (!roleToDelete) return;
    try {
      setLoader(true);
      await axios.delete(`/roles/${roleToDelete.id}`);
      toast.success("Role deleted successfully");
      await loadAdminUsers();
      setOpenRoleDeleteDialog(false);
      setRoleToDelete(null);
    } catch (error) {
      console.error("Error deleting role:", error);
      toast.error(
        error.response?.data?.message || "Failed to delete role"
      );
    } finally {
      setLoader(false);
    }
  };

  // 🔹 Cancel Delete Role
  const handleCancelDeleteRole = () => {
    setOpenRoleDeleteDialog(false);
    setRoleToDelete(null);
  };

  // 🔹 Save New Menu Access
  const saveNewMenuAccess = async () => {
    try {
      if (!newMenuAccess.menu_ids || newMenuAccess.menu_ids.length === 0) {
        toast.error("Please select at least one menu");
        return;
      }

      if (!newMenuAccess.permission_id) {
        toast.error("Please select a permission type");
        return;
      }

      setLoader(true);

      // Create payload - ensure permission_id is a number
      const payload = {
        role_id: Number(selectedUser.id),
        menu_id: newMenuAccess.menu_ids.map((id) => Number(id)),
        permission_id: Number(newMenuAccess.permission_id),
      };

      console.log("Sending payload:", payload);

      const response = await axios.post("/roleMenuRights", payload);

      console.log("Response:", response.data);

      toast.success(
        `Successfully added ${newMenuAccess.menu_ids.length} menu access permission(s)`,
      );

      await loadRoleMenuRights();
      handleCloseAddDialog();
    } catch (error) {
      toast.error("Failed to add menu access");
      console.error("Error adding menu access:", error);
      console.error("Error response:", error.response?.data);
    } finally {
      setLoader(false);
    }
  };

  // 🔹 Columns for Main Table
  const columns = [
    {
      name: "id",
      label: "S. No",
      options: {
        customBodyRenderLite: (dataIndex) => dataIndex + 1,
        filter: false,
      },
    },

    {
      name: "role_name",
      label: "Role",
      options: {
        filter: true,
        customBodyRenderLite: (dataIndex) => {
          const user = userList[dataIndex];
          return user?.role_name || "—";
        },
      },
    },

    {
      name: "permission",
      label: "Permission",
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex) => {
          const user = userList[dataIndex];
          return (
            <IconButton
              color="primary"
              size="small"
              onClick={() => handleOpenEditDialog(user)}
              title="Edit Menu Permissions"
            >
              <PlaylistAddIcon />
            </IconButton>
          );
        },
      },
    },

    {
      name: "delete",
      label: "Delete",
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex) => {
          const user = userList[dataIndex];
          return (
            <IconButton
              color="error"
              size="small"
              onClick={() => handleDeleteRole(user.id, user.role_name)}
              title="Delete Role"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          );
        },
      },
    },
  ];
  // 🔹 Columns for Menu Permissions Table in Dialog
  const menuPermissionColumns = [
    {
      name: "id",
      label: "S. No",
      options: {
        customBodyRenderLite: (dataIndex) => dataIndex + 1,
        filter: false,
      },
    },
    {
      name: "menu_name",
      label: "Menu Name",
      options: {
        customBodyRender: (value, tableMeta) => {
          const menus = getMenusForRole(selectedUser?.id || 0);
          const menuRight = menus[tableMeta.rowIndex];
          const menuHasChanges = hasChanges(menuRight, selectedUser?.id);

          return (
            <Box>
              <span>{value}</span>
              {menuHasChanges && (
                <span
                  style={{
                    color: "#1976d2",
                    fontSize: "12px",
                    fontWeight: "bold",
                    marginLeft: "8px",
                  }}
                >
                  (Modified)
                </span>
              )}
            </Box>
          );
        },
      },
    },
    {
      name: "permission_name",
      label: "Permission Type",
      options: {
        customBodyRender: (value, tableMeta) => {
          const menus = getMenusForRole(selectedUser?.id || 0);
          const menuRight = menus[tableMeta.rowIndex];
          return menuRight.permission?.permission_name || "N/A";
        },
      },
    },
    {
      name: "edit",
      label: "Edit",
      options: {
        filter: false,
        customBodyRender: (value, tableMeta) => {
          const menus = getMenusForRole(selectedUser?.id || 0);
          const menuRight = menus[tableMeta.rowIndex];

          return (
            <IconButton
              color="primary"
              size="small"
              onClick={() => handleEditPermission(menuRight)}
              title="Edit Permission"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          );
        },
      },
    },

    {
      name: "delete",
      label: "Delete",
      options: {
        filter: false,
        customBodyRender: (value, tableMeta) => {
          const menus = getMenusForRole(selectedUser?.id || 0);
          const menuRight = menus[tableMeta.rowIndex];

          const isChecked = isMenuSelected(selectedUser?.id, menuRight);

          return (
            <Checkbox
              size="small"
              checked={isChecked}
              onChange={() => toggleMenuSelection(selectedUser?.id, menuRight)}
            />
          );
        },
      },
    },
  ];

  // 🔹 Prepare data for menu permissions table
  const getMenuPermissionsTableData = () => {
    if (!selectedUser) return [];

    const menus = getMenusForRole(selectedUser.id);
    return menus.map((menuRight) => ({
      menu_name: menuRight.menu?.menu_name || `Menu ID: ${menuRight.menu_id}`,
      permission_name: menuRight.permission?.permission_name || "N/A",
      view: menuRight.permission?.view || 0,
      add: menuRight.permission?.add || 0,
      edit: menuRight.permission?.edit || 0,
      delete: menuRight.permission?.delete || 0,
    }));
  };

  // 🔹 Table Options
  const options = {
    selectableRows: "none",
    filter: false,
    search: true,
    download: false,
    print: false,
    viewColumns: true,
    rowsPerPage: 10,
    fixedHeader: true,
    textLabels: {
      body: {
        noMatch: loader ? "Loading..." : "No data available",
      },
    },
  };

  // 🔹 Menu Permissions Table Options
  const menuPermissionOptions = {
    selectableRows: "none",
    filter: false,
    search: true,
    download: false,
    print: false,
    viewColumns: false,
    pagination: false,
    fixedHeader: true,
    tableBodyHeight: tableHeight,
    elevation: 0,
    textLabels: {
      body: {
        noMatch: "No menus assigned to this role",
      },
    },
  };

  return (
    <>
      {loader ? (
        <Loader />
      ) : (
        <>
          <h4 className="text-dark">Role</h4>

          <MUIDataTable
            data={userList}
            columns={columns}
            options={options}
            title={
              <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={handleOpenNewRoleDialog}>
                New Role
              </Button>
            }
          />

          {/* Edit Menu Permissions Dialog */}
          <Dialog
            open={openEditDialog}
            onClose={handleCloseEditDialog}
            maxWidth="lg"
            fullWidth
          >
            <DialogTitle className="text-primary">
              Edit Menu Permissions - {selectedUser?.role_name}
            </DialogTitle>
            <DialogContent>
              {selectedUser && (
                <Box sx={{ mt: 2 }}>
                  <MUIDataTable
                    data={getMenuPermissionsTableData()}
                    columns={menuPermissionColumns}
                    options={menuPermissionOptions}
                    title={
                      getAvailableMenusForUser(selectedUser).length > 0 && (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => handleOpenAddDialog(selectedUser)}
                        >
                          Add Menu
                        </Button>
                      )
                    }
                  />
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              {getUserChanges(selectedUser?.id || 0).length > 0 && (
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={() => handleSaveClick(selectedUser.id)}
                >
                  Save Changes ({getUserChanges(selectedUser.id).length})
                </Button>
              )}
              {getSelectedMenusForUser(selectedUser?.id || 0).length > 0 && (
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleDeleteClick(selectedUser.id)}
                >
                  Delete Selected (
                  {getSelectedMenusForUser(selectedUser.id).length})
                </Button>
              )}
              <Button
                onClick={handleCloseEditDialog}
                variant="contained"
                color="error"
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Add Menu Access Dialog */}
          <Dialog
            open={openAddDialog}
            onClose={handleCloseAddDialog}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              Add Menu Access for {selectedUser?.role_name}
            </DialogTitle>
            <DialogContent>
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}
              >
                <FormControl fullWidth>
                  <InputLabel>Select Menus</InputLabel>
                  <Select
                    multiple
                    value={newMenuAccess.menu_ids}
                    label="Select Menus"
                    onChange={(e) =>
                      setNewMenuAccess((prev) => ({
                        ...prev,
                        menu_ids: e.target.value,
                      }))
                    }
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return <em>Select menus</em>;
                      }
                      const selectedNames = selected.map((id) => {
                        const menu = menuList.find((m) => m.menu_id === id);
                        return menu?.menu_name || `Menu ${id}`;
                      });
                      return selectedNames.join(", ");
                    }}
                  >
                    {selectedUser &&
                      getAvailableMenusForUser(selectedUser).map((menu) => (
                        <MenuItem key={menu.menu_id} value={menu.menu_id}>
                          <Checkbox
                            checked={
                              newMenuAccess.menu_ids.indexOf(menu.menu_id) > -1
                            }
                          />
                          {menu.menu_name || `Menu ${menu.menu_id}`}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Select Permission Type</InputLabel>
                  <Select
                    value={newMenuAccess.permission_id}
                    label="Select Permission Type"
                    onChange={(e) =>
                      handlePermissionSelectionChange(e.target.value)
                    }
                  >
                    {permissionsList.map((permission) => (
                      <MenuItem
                        key={permission.permission_id}
                        value={permission.permission_id}
                      >
                        {permission.permission_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleCloseAddDialog}
                variant="contained"
                color="error"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={saveNewMenuAccess}
                startIcon={<SaveIcon />}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>

          {/* Save Confirmation Dialog */}
          <Dialog
            open={openSaveDialog}
            onClose={handleCancelSave}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Confirm Save Changes</DialogTitle>
            <DialogContent>
              <p>
                Are you sure you want to save all the changes made to menu
                permissions?
              </p>
              {pendingAction && (
                <p style={{ color: "#1976d2", fontWeight: "bold" }}>
                  {getUserChanges(pendingAction.userId).length} menu
                  permission(s) will be updated.
                </p>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleCancelSave}
                variant="contained"
                color="error"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleConfirmSave}
                startIcon={<SaveIcon />}
              >
                Confirm Save
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={openDeleteDialog}
            onClose={handleCancelDelete}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
              <p>
                Are you sure you want to delete the selected menu permissions?
              </p>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleCancelDelete}
                variant="contained"
                color="primary"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleConfirmDelete}
                startIcon={<DeleteIcon />}
              >
                Confirm Delete
              </Button>
            </DialogActions>
          </Dialog>

          {/* Edit Permission Dialog */}
          <Dialog
            open={openEditPermissionDialog}
            onClose={handleCloseEditPermissionDialog}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              Edit Permission for {selectedMenuRight?.menu?.menu_name}
            </DialogTitle>
            <DialogContent>
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}
              >
                <FormControl fullWidth>
                  <InputLabel>Select Permission Type</InputLabel>
                  <Select
                    value={editMenuPermission.permission_id}
                    label="Select Permission Type"
                    onChange={(e) =>
                      handleEditPermissionSelectionChange(e.target.value)
                    }
                  >
                    {permissionsList.map((permission) => (
                      <MenuItem
                        key={permission.permission_id}
                        value={permission.permission_id}
                      >
                        {permission.permission_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Display selected permissions (read-only) */}
                {/* {editMenuPermission.permission_id && (
                  <Box
                    sx={{ mt: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}
                  >
                    <h6 style={{ marginBottom: "10px" }}>
                      Selected Permissions:
                    </h6>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 1,
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={editMenuPermission.view === 1}
                            color="primary"
                            disabled
                          />
                        }
                        label="View"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={editMenuPermission.add === 1}
                            color="success"
                            disabled
                          />
                        }
                        label="Add"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={editMenuPermission.edit === 1}
                            color="warning"
                            disabled
                          />
                        }
                        label="Edit"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={editMenuPermission.delete === 1}
                            color="error"
                            disabled
                          />
                        }
                        label="Delete"
                      />
                    </Box>
                  </Box>
                )} */}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleCloseEditPermissionDialog}
                variant="contained"
                color="error"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={saveEditPermission}
                startIcon={<SaveIcon />}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>

          {/* Create New Role Dialog */}
          <Dialog open={open} onClose={handleCloseNewRoleDialog} fullWidth>
            <DialogTitle className="text-primary">
              Create New Role
            </DialogTitle>

            <DialogContent>
              <TextField
                fullWidth
                margin="dense"
                label="Role Name"
                name="role_name"
                value={newRoleFormData.role_name}
                onChange={handleNewRoleChange}
                placeholder="e.g., Admin"
              />

              <TextField
                fullWidth
                margin="dense"
                label="Description"
                name="description"
                value={newRoleFormData.description}
                onChange={handleNewRoleChange}
                placeholder="e.g., Admin, who owns all administration access"
                multiline
                rows={3}
              />

              <FormControl fullWidth margin="dense">
                <InputLabel>Active Status</InputLabel>
                <Select
                  name="active_status"
                  value={newRoleFormData.active_status}
                  onChange={handleNewRoleChange}
                  label="Active Status"
                >
                  <MenuItem value={1}>Active</MenuItem>
                  <MenuItem value={0}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </DialogContent>

            <DialogActions>
              <Button
                onClick={handleCloseNewRoleDialog}
                variant="contained" color="error"
              >
                Cancel
              </Button>
              <Button
                color="primary"
                variant="contained"
                onClick={handleCreateRole}
                startIcon={<SaveIcon />}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Role Confirmation Dialog */}
          <Dialog
            open={openRoleDeleteDialog}
            onClose={handleCancelDeleteRole}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Confirm Delete Role</DialogTitle>
            <DialogContent>
              <p>
                Are you sure you want to delete the role "<strong>{roleToDelete?.name}</strong>"?
              </p>
              
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCancelDeleteRole} variant="contained" color="error">
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDeleteRole}
                variant="contained"
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </>
  );
};

export default Role;
